import type {
  RecommendationPlan,
  DeduplicatedPlan,
  NetworkOperator,
  DualEngineOffer,
  DualEngineResult,
} from "./types"

/** Translation function type — matches the t() returned by useTranslation */
export type TFunc = (key: string, opts?: Record<string, unknown>) => string

// =============================================================================
// Constants
// =============================================================================

// Days: 1-30 continuous
export const DAY_STOPS = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21,
  22, 23, 24, 25, 26, 27, 28, 29, 30,
]

// All quota values (MB) accepted by the backend API (union of Telna + CMLink white-lists).
// Used to snap user input to the nearest supported value before sending API requests.
export const API_SUPPORTED_QUOTAS = [
  // < 1 GB (MB values)
  100, 200, 300, 400, 500, 512, 600, 700, 800, 900, 1000,
  // 1-20 GB (both Telna and CMLink)
  1024, 2048, 3072, 4096, 5120, 6144, 7168, 8192, 9216, 10240,
  11264, 12288, 13312, 14336, 15360, 16384, 17408, 18432, 19456, 20480,
  // 21-50 GB (CMLink only) — N * 1024
  24576, 25600, 28672, 30720, 31744, 33792, 35840, 36864, 40960, 46080,
  49152, 51200,
]

// Quota in MB: 100-500 MB, 1-50 GB, Unlimited(-1)
export const QUOTA_STOPS = [
  100, 200, 300, 400, 500, 1024, 2048, 3072, 4096, 5120, 6144, 7168, 8192,
  9216, 10240, 11264, 12288, 13312, 14336, 15360, 16384, 17408, 18432, 19456,
  20480, -1,
]


export const API_BASE = "/api/store/recommendations"

export const SMART_API_BASE = "/api/store/smart-recommendations"

// Trip type presets for filter bar
export type TripType = "short" | "standard" | "extended" | "unlimited"
export type SortBy = "price-asc" | "price-desc" | "data-desc"

export const TRIP_TYPE_PARAMS: Record<
  TripType,
  { day: number; quota: number }
> = {
  short: { day: 3, quota: 3072 },
  standard: { day: 7, quota: 10240 },
  extended: { day: 14, quota: 20480 },
  unlimited: { day: 7, quota: -1 },
}

// =============================================================================
// Validation & Normalization
// =============================================================================

function isValidNumber(n: unknown): n is number {
  return typeof n === "number" && isFinite(n)
}

/**
 * Normalize API plan data: convert string "unlimited" quota to -1 number.
 * The API returns quota as "unlimited" (string) but our code expects -1 (number).
 */
export function normalizePlans(plans: RecommendationPlan[]): RecommendationPlan[] {
  for (const plan of plans) {
    // quota: "unlimited" (string) → -1 (number)
    if ((plan.data.quota as unknown) === "unlimited" || (plan.data.quota as unknown) === "Unlimited") {
      plan.data.quota = -1
    } else if (typeof (plan.data.quota as unknown) === "string") {
      // Some providers (e.g. "N") return quota as a numeric string. Coerce so === comparisons work.
      const parsed = Number(plan.data.quota)
      plan.data.quota = Number.isFinite(parsed) ? parsed : plan.data.quota
    }
    // day: some providers return "7" (string) instead of 7 (number).
    // Strict equality with a numeric userDay would silently drop those plans —
    // this is how the N-provider unlimited-with-phoneNumber card was disappearing.
    if (typeof (plan.data.day as unknown) === "string") {
      const parsed = Number(plan.data.day)
      if (Number.isFinite(parsed)) plan.data.day = parsed
    }
  }
  return plans
}

/**
 * Snap a quota value (MB) to the nearest value accepted by the backend API.
 * The backend only accepts specific quota values from TelnaSupportSkuOptions ∪ CmlinkSupportSkuOptions.
 * Returns "unlimited" for -1; otherwise the nearest supported MB value.
 */
export function snapQuotaForAPI(quotaMB: number): number | string {
  if (quotaMB === -1) return "unlimited"
  if (quotaMB <= 0) return API_SUPPORTED_QUOTAS[0]

  // Find the nearest value >= quotaMB (prefer not to under-deliver)
  const upper = API_SUPPORTED_QUOTAS.find((q) => q >= quotaMB)
  if (upper !== undefined) return upper

  // If quotaMB exceeds all supported values, use the largest
  return API_SUPPORTED_QUOTAS[API_SUPPORTED_QUOTAS.length - 1]
}

// =============================================================================
// Formatting Helpers
// =============================================================================

export function iso2ToFlag(iso2: string): string {
  if (iso2.length !== 2) return "\u{1F3F3}\u{FE0F}"
  const codePoints = iso2
    .toUpperCase()
    .split("")
    .map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)
  return String.fromCodePoint(...codePoints)
}

/** Returns a flag SVG URL from flagcdn.com — vector, sharp at any size */
export function getFlagUrl(iso2: string): string {
  return `https://flagcdn.com/${iso2.toLowerCase()}.svg`
}

// =============================================================================
// Region-aware presets
// =============================================================================

type RegionPreset = {
  /** ISO2 codes of nearby popular countries */
  nearby: [string, string]
  /** Multi-country combo for the region */
  multi: { iso2s: string[]; labelKey: string }
}

const REGION_MAP: Record<string, string> = {
  JP: "asia", KR: "asia", CN: "asia", TW: "asia", HK: "asia", SG: "asia",
  TH: "asia", VN: "asia", MY: "asia", ID: "asia", PH: "asia", KH: "asia",
  MM: "asia", LA: "asia", BN: "asia", TL: "asia", MN: "asia", IN: "asia",
  GB: "europe", FR: "europe", DE: "europe", IT: "europe", ES: "europe",
  PT: "europe", NL: "europe", BE: "europe", CH: "europe", AT: "europe",
  SE: "europe", NO: "europe", DK: "europe", FI: "europe", IE: "europe",
  PL: "europe", CZ: "europe", GR: "europe", HR: "europe", HU: "europe",
  RO: "europe", BG: "europe", RS: "europe", SK: "europe", SI: "europe",
  LT: "europe", LV: "europe", EE: "europe", UA: "europe", RU: "europe",
  IS: "europe", AL: "europe", BA: "europe", ME: "europe", MK: "europe",
  US: "americas", CA: "americas", MX: "americas", BR: "americas",
  AR: "americas", CL: "americas", CO: "americas", PE: "americas",
  EC: "americas", CR: "americas", PA: "americas", DO: "americas",
  AU: "oceania", NZ: "oceania", FJ: "oceania",
  AE: "mideast", SA: "mideast", IL: "mideast", TR: "mideast",
  QA: "mideast", KW: "mideast", BH: "mideast", OM: "mideast",
  JO: "mideast", LB: "mideast", EG: "mideast",
  ZA: "africa", MA: "africa", KE: "africa", NG: "africa", GH: "africa",
  TZ: "africa", TN: "africa", MU: "africa",
}

const REGION_PRESETS: Record<string, RegionPreset> = {
  asia: { nearby: ["KR", "SG"], multi: { iso2s: ["KR", "TH", "SG", "TW", "HK"], labelKey: "step-preset-multi-asia" } },
  europe: { nearby: ["FR", "DE"], multi: { iso2s: ["FR", "DE", "IT", "ES", "NL"], labelKey: "step-preset-multi-europe" } },
  americas: { nearby: ["CA", "MX"], multi: { iso2s: ["US", "CA", "MX", "BR", "AR"], labelKey: "step-preset-multi-americas" } },
  oceania: { nearby: ["NZ", "SG"], multi: { iso2s: ["AU", "NZ", "ID", "TH", "SG"], labelKey: "step-preset-multi-oceania" } },
  mideast: { nearby: ["AE", "TR"], multi: { iso2s: ["AE", "TR", "SA", "IL", "QA"], labelKey: "step-preset-multi-mideast" } },
  africa: { nearby: ["ZA", "EG"], multi: { iso2s: ["ZA", "EG", "MA", "KE", "NG"], labelKey: "step-preset-multi-africa" } },
}

/** Returns region-aware quick presets for the TripConfigurator country picker */
export function getRegionPresets(iso2: string): RegionPreset {
  const region = REGION_MAP[iso2.toUpperCase()] || "asia"
  return REGION_PRESETS[region] || REGION_PRESETS.asia
}

/**
 * Format a quota value for display.
 * @param quotaMB - quota in MB; -1 means unlimited
 * @param unlimitedLabel - pre-translated "Unlimited" string; defaults to "Unlimited"
 */
export function formatQuota(quotaMB: number, unlimitedLabel = "Unlimited"): string {
  if (!isValidNumber(quotaMB)) return ""
  if (quotaMB === -1) return unlimitedLabel
  if (quotaMB < 1024) return `${quotaMB} MB`
  const gb = quotaMB / 1024
  return gb % 1 === 0 ? `${gb} GB` : `${gb.toFixed(1)} GB`
}

export function formatPrice(cents: number): string {
  if (!isValidNumber(cents)) return "—"
  return (cents / 100).toFixed(2)
}

/**
 * Returns per-GB price string, or null if not applicable.
 * Returns null for daily plans (daily quota resets each day, per-GB is misleading).
 */
export function getUnitPrice(
  cents: number,
  quotaMB: number,
  isDaily?: boolean
): string | null {
  if (isDaily) return null // Daily plans: per-GB makes no sense
  if (!isValidNumber(cents) || !isValidNumber(quotaMB)) return null
  if (quotaMB <= 0 || quotaMB >= 102400) return null
  const gb = quotaMB / 1024
  if (gb <= 0) return null
  const result = (cents / 100 / gb).toFixed(2)
  if (result === "NaN" || result === "Infinity") return null
  return result
}

// =============================================================================
// Data Extraction Helpers
// =============================================================================

export function getOperatorsForCountries(
  network: Record<string, NetworkOperator[]>,
  iso2s: string[]
): { operator: string; has5G: boolean }[] {
  const seen = new Set<string>()
  const result: { operator: string; has5G: boolean }[] = []
  for (const code of iso2s) {
    const ops = network[code]
    if (!ops) continue
    for (const op of ops) {
      if (seen.has(op.operator)) continue
      seen.add(op.operator)
      result.push({
        operator: op.operator,
        has5G: op.speed.some((s) => s.includes("5G")),
      })
    }
  }
  return result
}

export function getOperatorNames(
  plans: RecommendationPlan[],
  iso2: string
): string[] {
  const names = new Set<string>()
  for (const plan of plans) {
    const ops = plan.data.network[iso2] || []
    for (const op of ops) {
      names.add(op.operator)
    }
  }
  return Array.from(names)
}

export function getLowestPrice(plans: RecommendationPlan[]): number {
  if (plans.length === 0) return 0
  return Math.min(...plans.map((p) => p.amount))
}

export function getHighestPrice(plans: RecommendationPlan[]): number {
  if (plans.length === 0) return 0
  return Math.max(...plans.map((p) => p.amount))
}

export function has5GSupport(plans: RecommendationPlan[]): boolean {
  return plans.some((p) =>
    Object.values(p.data.network)
      .flat()
      .some((op) => op.speed.some((s) => s.includes("5G")))
  )
}

// =============================================================================
// Hero region inference — for H1 dynamic wording
// =============================================================================

/**
 * Override the i18n source name for ISO2s where oldData only has a "packaging"
 * variant (e.g. "Vietnam Unlimited") and we want the country list to display
 * the plain country name instead. Used by /buy and the product page country
 * picker to keep names consistent across locales.
 */
export const ISO2_DISPLAY_NAME_OVERRIDE: Record<string, string> = {
  VN: "Vietnam",
}

export type HeroRegionId =
  | "asia"
  | "middleEast"
  | "europe"
  | "northAmerica"
  | "latinAmerica"
  | "africa"
  | "oceania"

const HERO_REGIONS: ReadonlyArray<{ id: HeroRegionId; iso2s: ReadonlySet<string> }> = [
  {
    id: "asia",
    iso2s: new Set([
      "JP","KR","CN","HK","TW","MO","TH","VN","ID","MY","SG","PH","IN",
      "KH","LA","MM","BN","LK","BD","NP","PK","MN","KZ","UZ","KG","TJ","TM",
    ]),
  },
  {
    id: "middleEast",
    iso2s: new Set([
      "AE","SA","IL","QA","OM","KW","BH","TR","JO","LB","IQ","IR","SY","YE",
    ]),
  },
  {
    id: "europe",
    iso2s: new Set([
      "GB","DE","FR","IT","ES","NL","CH","AT","BE","PT","SE","NO","DK","FI",
      "IE","PL","CZ","HU","GR","RO","BG","HR","SI","SK","LT","LV","EE","LU",
      "MT","CY","IS","LI","MC","AD","SM","VA","RU","UA","BY","MD","AL","BA",
      "RS","ME","MK","XK",
    ]),
  },
  {
    id: "northAmerica",
    iso2s: new Set(["US","CA"]),
  },
  {
    id: "latinAmerica",
    iso2s: new Set([
      "MX","BR","AR","CL","CO","PE","VE","EC","UY","PY","BO","CR","PA","GT",
      "HN","NI","SV","DO","JM","CU","HT","TT","BS","BB",
    ]),
  },
  {
    id: "africa",
    iso2s: new Set([
      "ZA","EG","KE","NG","MA","TN","DZ","GH","ET","TZ","UG","RW","SN","CI","CM",
    ]),
  },
  {
    id: "oceania",
    iso2s: new Set(["AU","NZ","FJ","PG","NC","PF","WS","TO"]),
  },
]

/**
 * Infer a single region id from a list of iso2 codes.
 * Returns the region id only if ALL codes are in the same region.
 * Returns null if codes are empty or span multiple regions.
 */
export function inferRegionId(iso2s: string[]): HeroRegionId | null {
  if (iso2s.length === 0) return null
  for (const region of HERO_REGIONS) {
    if (iso2s.every((c) => region.iso2s.has(c))) {
      return region.id
    }
  }
  return null
}

// =============================================================================
// Deduplication
// =============================================================================

export function deduplicatePlans(
  plans: RecommendationPlan[]
): DeduplicatedPlan[] {
  const map = new Map<string, DeduplicatedPlan>()

  for (const plan of plans) {
    // Skip plans with invalid data
    if (!plan.amount || plan.amount <= 0) continue
    if (plan.data.quota === 0) continue

    const daily = plan.data.daily ?? false
    const hotspot = plan.tags?.hotspot ?? false
    const phoneNumber = plan.tags?.phoneNumber ?? false
    const topup = plan.tags?.topup ?? false
    const hasYohoCare =
      plan.tags?.yohoCare === true ||
      plan.provider === "CMLink" ||
      plan.provider === "C"
    const operators = Object.values(plan.data.network ?? {})
      .flat()
      .map((n) => n.operator)
      .sort()
      .join(",")
    const key = [
      plan.data.quota,
      plan.data.day,
      daily,
      hotspot,
      phoneNumber,
      topup,
      hasYohoCare,
      operators,
    ].join("|")
    const existing = map.get(key)

    if (!existing) {
      map.set(key, { ...plan, alternativeProviders: [] })
    } else {
      if (plan.amount < existing.amount) {
        // New plan is cheaper — promote it, demote existing
        existing.alternativeProviders.push({
          provider: existing.provider,
          amount: existing.amount,
          network: existing.data.network,
        })
        // Copy new plan fields over
        existing.provider = plan.provider
        existing.amount = plan.amount
        existing.sg_amount = plan.sg_amount
        existing.data = plan.data
        existing.tags = plan.tags
      } else {
        // Existing is cheaper — store new as alternative
        existing.alternativeProviders.push({
          provider: plan.provider,
          amount: plan.amount,
          network: plan.data.network,
        })
      }
    }
  }

  return Array.from(map.values())
}

// =============================================================================
// =============================================================================
// Tag Computation
// =============================================================================

export function computeTagBadges(plan: RecommendationPlan): string[] {
  const badges: string[] = []
  const tags = plan.tags

  // Priority 1 — value props
  if (tags?.cheapest) badges.push("Best Value")
  if (tags?.promotion) badges.push("Popular")
  // Note: "Unlimited" is already shown via getPlanTypeLabel — don't duplicate here

  // Priority 2 — capability badges (align with Flutter recommendation_component.dart:393)
  // Flutter rule: showYohocare = provider == 'C' || tags['yohoCare']
  const hasYohoCare =
    tags?.yohoCare === true ||
    plan.provider === "CMLink" ||
    plan.provider === "C"
  if (hasYohoCare && badges.length < 4) badges.push("Yoho Care")
  if (tags?.hotspot && badges.length < 4) badges.push("Hotspot")
  if (tags?.topup && badges.length < 4) badges.push("Top-up Available")
  if (tags?.phoneNumber && badges.length < 4) badges.push("Phone Number")

  return badges.slice(0, 4)
}

// =============================================================================
// Plan Display Spec Helpers
// =============================================================================

/**
 * getPlanDisplaySpec — returns formatted quota + day string for display.
 * e.g. "10GB｜7天" or "每日 1GB｜7天" for zh-CN
 */
export function getPlanDisplaySpec(
  plan: RecommendationPlan,
  lng?: string
): string {
  const isCN = lng === "zh-CN"
  const quota = plan.data.quota
  const day = plan.data.day
  const daily = plan.data.daily

  const quotaStr = formatQuota(quota, isCN ? "无限流量" : "Unlimited")
  if (!quotaStr) return ""

  if (isCN) {
    if (daily) return `每日 ${quotaStr}｜${day}天`
    return `${quotaStr}｜${day}天`
  }
  const dayStr = day === 1 ? "1 day" : `${day} days`
  if (daily) return `${quotaStr}/day｜${dayStr}`
  return `${quotaStr}｜${dayStr}`
}

/**
 * getPlanScopeLabel — returns a stable key for multi-country scope tag.
 * Returns "multi-country" when the plan covers >1 country, null otherwise.
 * PlanCard is responsible for translating via t("plan-scope-multi-country").
 */
export function getPlanScopeLabel(
  plan: RecommendationPlan,
  _lng?: string
): "multi-country" | null {
  const countries = plan.data.support_countries
  if (!countries || countries.length <= 1) return null
  return "multi-country"
}

/**
 * getPlanTypeLabel — returns a stable key for plan type tag.
 * Returns "unlimited" | "daily" | null.
 * PlanCard is responsible for translating via t("plan-type-unlimited") etc.
 */
export function getPlanTypeLabel(
  plan: RecommendationPlan,
  _lng?: string
): "unlimited" | "daily" | null {
  if (plan.data.quota === -1) return "unlimited"
  if (plan.data.daily) return "daily"
  return null
}

/**
 * getPlanReason — returns a short purchase reason string for zh-CN.
 */
export function getPlanReason(
  plan: RecommendationPlan,
  lng?: string
): string {
  const isCN = lng === "zh-CN"
  const quota = plan.data.quota
  const day = plan.data.day
  const daily = plan.data.daily
  const multiCountry =
    plan.data.support_countries && plan.data.support_countries.length > 1

  const hotspot = plan.tags?.hotspot ?? false

  if (isCN) {
    if (quota === -1) return hotspot ? "适合重度上网或多设备共享" : "适合重度上网需求"
    if (multiCountry) return "适合多国路线，一卡覆盖"
    if (daily && quota >= 2048) return "每日流量充足，适合中重度使用"
    if (daily) return "每日流量有限，适合轻度使用"
    if (day > 30) return "适合长期居留或出差"
    if (day >= 14) return "更适合中长行程"
    if (quota >= 10240) return "适合大多数旅行用户"
    if (quota <= 3072 && day <= 5) return "适合短途或轻度使用"
    return "适合大多数旅行用户"
  }
  if (quota === -1) return hotspot ? "Best for heavy use or device sharing" : "Best for heavy internet use"
  if (multiCountry) return "Great for multi-country trips"
  if (daily && quota >= 2048) return "Generous daily data for moderate-heavy use"
  if (daily) return "Daily data for light use"
  if (day > 30) return "Best for long-term stays or work trips"
  if (day >= 14) return "Best for extended stays"
  if (quota >= 10240) return "Popular choice for most travelers"
  return "Good value for your trip"
}

// =============================================================================
// Dual Engine — Offer Resolver
// =============================================================================

/**
 * Build the API countries parameter. Single country uses ISO2 directly;
 * multiple countries use underscore separator (API requirement).
 */
export function buildCountriesParam(iso2s: string[]): string {
  return iso2s.sort().join("_")
}

/**
 * Determine search parameter variants for inventory discovery.
 * Searches BOTH directions (lower quota may be cheaper, higher may be multi-country
 * bundle deals). Also searches unlimited as Joytel often has unlimited daily plans.
 * The backend returns the cheapest variant per provider that meets >= the requested
 * quota/day, so querying with *lower* quotas discovers cheaper Joytel/T2 variants
 * that still satisfy the user's actual needs when evaluated in resolveDualEngine.
 */
export function getInventorySearchParams(
  userQuota: number,
  userDay: number,
  userDaily: boolean
): Array<{ quota: number | string; day: number }> {
  const params: Array<{ quota: number | string; day: number }> = []
  const seen = new Set<string>()
  const add = (q: number | string, d: number) => {
    const key = `${q}_${d}`
    if (!seen.has(key)) { seen.add(key); params.push({ quota: q, day: d }) }
  }

  // Unlimited at user's exact day — may be cheaper than finite quota.
  if (!userDaily && userQuota !== -1) {
    add("unlimited", userDay)
  }

  // Adjacent days for fallback when exact day has no inventory.
  // Search common day values near userDay to find closest available plans.
  const commonDays = [1, 3, 5, 7, 8, 9, 10, 14, 15, 20, 25, 27, 28, 30, 60, 90, 180, 365]
  const nearby = commonDays
    .filter((d) => d !== userDay && Math.abs(d - userDay) <= Math.max(userDay * 0.3, 5))
    .sort((a, b) => Math.abs(a - userDay) - Math.abs(b - userDay))
    .slice(0, 3)
  for (const d of nearby) {
    add(userQuota === -1 ? "unlimited" : userQuota, d)
    if (!userDaily && userQuota !== -1) add("unlimited", d)
  }

  return params
}

/**
 * Check hard constraints: plan must cover all user-selected countries,
 * have days >= user input, and data >= user input.
 */
function meetsHardConstraints(
  plan: RecommendationPlan,
  userIso2s: string[],
  userDay: number,
  userQuota: number
): boolean {
  // Must cover all user-selected countries
  const planCountries = new Set(
    plan.data.support_countries?.map((c) => c.toUpperCase()) || []
  )
  for (const iso2 of userIso2s) {
    if (!planCountries.has(iso2.toUpperCase())) return false
  }
  // Days must exactly match user input — never recommend longer durations
  if (plan.data.day !== userDay) return false
  // Data must be >= user input (unlimited always passes)
  if (userQuota === -1) {
    if (plan.data.quota !== -1) return false
  } else if (plan.data.quota !== -1 && plan.data.quota < userQuota) {
    return false
  }
  return true
}

/**
 * Relaxed constraints for near-matches: same countries, data >= request,
 * but days within ±30% or ±5 (whichever is larger).
 */
function meetsNearConstraints(
  plan: RecommendationPlan,
  userIso2s: string[],
  userDay: number,
  userQuota: number
): boolean {
  const planCountries = new Set(
    plan.data.support_countries?.map((c) => c.toUpperCase()) || []
  )
  for (const iso2 of userIso2s) {
    if (!planCountries.has(iso2.toUpperCase())) return false
  }
  // Days within ±30% or ±5 of user request (whichever is larger), and plan day >= 1
  const tolerance = Math.max(userDay * 0.3, 5)
  if (Math.abs(plan.data.day - userDay) > tolerance) return false
  if (plan.data.day === userDay) return false // exact match handled separately
  // Data must be >= user input
  if (userQuota === -1) {
    if (plan.data.quota !== -1) return false
  } else if (plan.data.quota !== -1 && plan.data.quota < userQuota) {
    return false
  }
  return true
}

/**
 * Generate explanation text for why a plan is shown.
 */
function explainOffer(
  plan: RecommendationPlan,
  userIso2s: string[],
  userDay: number,
  userQuota: number,
  t: TFunc,
  avgExactPrice: number
): { explanation: string; savingsPercent?: number; extraCountries?: string[]; extraDays?: number; extraData?: number } {
  const extraCountries = (plan.data.support_countries || []).filter(
    (c) => !userIso2s.includes(c.toUpperCase()) && !userIso2s.includes(c)
  )
  const extraDays = plan.data.day > userDay ? plan.data.day - userDay : 0
  const extraData =
    plan.data.quota !== -1 && userQuota > 0 && plan.data.quota > userQuota
      ? plan.data.quota - userQuota
      : 0
  const savingsPercent =
    avgExactPrice > 0
      ? Math.round(((avgExactPrice - plan.amount) / avgExactPrice) * 100)
      : undefined

  const parts: string[] = []

  if (savingsPercent && savingsPercent > 0) {
    parts.push(t("offer-savings-percent", { percent: savingsPercent }))
  }
  if (extraCountries.length > 0) {
    parts.push(t("offer-extra-countries", { count: extraCountries.length }))
  }
  if (extraDays > 0) {
    parts.push(t("offer-extra-days", { count: extraDays }))
  }
  if (extraData > 0) {
    parts.push(t("offer-extra-data", { data: formatQuota(extraData) }))
  }
  if (plan.data.quota === -1 && userQuota !== -1) {
    parts.push(t("offer-upgraded-unlimited"))
  }

  return {
    explanation: parts.join(t("offer-sep")) || t("offer-matches-request"),
    savingsPercent: savingsPercent && savingsPercent > 0 ? savingsPercent : undefined,
    extraCountries: extraCountries.length > 0 ? extraCountries : undefined,
    extraDays: extraDays > 0 ? extraDays : undefined,
    extraData: extraData > 0 ? extraData : undefined,
  }
}

/**
 * Generate explanation text for an unlimited upgrade nudge.
 * Uses per-day upgrade cost framing instead of fake discounts.
 */
function explainUpgradeOffer(
  perDayCents: number,
  userDay: number,
  isDaily: boolean,
  t: TFunc
): string {
  if (perDayCents <= 0) {
    return isDaily
      ? t("upgrade-lower-price-daily")
      : t("upgrade-lower-price")
  }
  const cost = (perDayCents / 100).toFixed(2)
  if (perDayCents < 100) {
    return isDaily
      ? t("upgrade-per-day-cheap-daily", { cost })
      : t("upgrade-per-day-cheap", { cost })
  }
  return isDaily
    ? t("upgrade-per-day-trip-daily", { cost, days: userDay })
    : t("upgrade-per-day-trip", { cost, days: userDay })
}

/**
 * Core dual-engine resolver: takes all fetched plans (from multiple API calls)
 * and categorizes them into exact matches, inventory opportunities, and alternatives.
 */
export function resolveDualEngine(
  allPlans: RecommendationPlan[],
  userIso2s: string[],
  userDay: number,
  userQuota: number,
  userDaily: boolean,
  t: TFunc
): DualEngineResult {
  // Deduplicate by provider+quota+day+daily+amount
  const seen = new Map<string, RecommendationPlan>()
  for (const plan of allPlans) {
    if (!plan.amount || plan.amount <= 0) continue
    const key = `${plan.provider}_${plan.data.quota}_${plan.data.day}_${plan.data.daily}_${plan.amount}`
    if (!seen.has(key) || plan.amount < seen.get(key)!.amount) {
      seen.set(key, plan)
    }
  }
  const uniquePlans = Array.from(seen.values())

  // Gather ALL plans that meet hard constraints (regardless of strictMatched)
  const qualified: RecommendationPlan[] = []

  for (const plan of uniquePlans) {
    if (!meetsHardConstraints(plan, userIso2s, userDay, userQuota)) continue
    if (userDaily && !plan.data.daily) continue
    if (!userDaily && userQuota !== -1 && plan.data.daily) continue
    qualified.push(plan)
  }
  qualified.sort((a, b) => a.amount - b.amount)

  // Reference price: cheapest strictMatched plan (for savings %)
  const strictPlans = qualified.filter((p) => p.tags?.strictMatched)
  // Use cheapest strict plan as reference; if none, use median of qualified
  const refPrice = strictPlans.length > 0
    ? strictPlans[0].amount // already sorted by price
    : qualified.length >= 3
      ? qualified[Math.floor(qualified.length / 2)].amount
      : qualified.length > 0
        ? qualified[0].amount
        : 0

  // ── Pick up to 3 recommended cards with distinct value propositions ──
  const picked = new Set<string>()
  const pickedKey = (p: RecommendationPlan) =>
    `${p.provider}_${p.data.quota}_${p.data.day}_${p.data.daily}_${p.amount}`

  const exactMatches: DualEngineOffer[] = []

  // Slot 1 — User's exact match: cheapest strictMatched plan (single-country direct)
  const userExact = strictPlans[0]
  if (userExact) {
    // If user asked for fixed quota but got unlimited, explain the upgrade
    const isUpgradeToUnlimited = userExact.data.quota === -1 && userQuota !== -1
    const slot1Explanation = isUpgradeToUnlimited
      ? t("offer-no-exact-upgraded", { quota: formatQuota(userQuota) })
      : t("offer-recommended")
    exactMatches.push({
      plan: userExact,
      tier: "exact",
      explanation: slot1Explanation,
      highlight: true,
    })
    picked.add(pickedKey(userExact))
  }

  // Slot 2 — Best overall: cheapest plan across all qualified (may be multi-country)
  const bestOverall = qualified.find((p) => !picked.has(pickedKey(p)))
  if (bestOverall) {
    const info = explainOffer(bestOverall, userIso2s, userDay, userQuota, t, refPrice)
    const isMulti = (bestOverall.data.support_countries?.length ?? 0) > 1
    const isCheaper = userExact && bestOverall.amount < userExact.amount
    let explanation: string
    if (isCheaper && isMulti) {
      explanation = t("offer-cheaper-multi", {
        percent: info.savingsPercent ?? 0,
        count: bestOverall.data.support_countries!.length,
        hasSavings: info.savingsPercent ? "1" : "",
      })
    } else if (isCheaper) {
      explanation = t("offer-cheaper-overall", {
        percent: info.savingsPercent ?? 0,
        hasSavings: info.savingsPercent ? "1" : "",
      })
    } else if (isMulti) {
      explanation = t("offer-multi-similar-price", { count: bestOverall.data.support_countries!.length })
    } else {
      explanation = info.explanation
    }
    exactMatches.push({
      plan: bestOverall,
      tier: "exact",
      explanation,
      highlight: !userExact, // highlight only if Slot 1 is empty
      savingsPercent: info.savingsPercent,
      extraCountries: info.extraCountries,
    })
    picked.add(pickedKey(bestOverall))
  }

  // Slot 3 — REMOVED: differentiated feature cards (phone number, special support, hotspot)
  // These will now appear in inventory offers section instead of exactMatches

  // ── Inventory offers: cheaper alternatives OR differentiated features ──
  // Search across ALL uniquePlans (not just qualified) to discover:
  // 1. Cheaper unlimited plans (total price lower than exactMatches)
  // 2. Daily plans that could be cheaper (even if user chose total mode)
  // 3. Plans with unique features (phone number, hotspot) at reasonable price
  // If nothing qualifies, show nothing — don't force recommendations.
  const inventoryOffers: DualEngineOffer[] = []
  const cheapestExact = exactMatches.length > 0
    ? Math.min(...exactMatches.map((o) => o.plan.amount))
    : Infinity

  // Helper: plan covers user's countries, matches day count, and meets quota
  const meetsBasic = (plan: RecommendationPlan): boolean => {
    if (!plan.amount || plan.amount <= 0) return false
    if (plan.data.day !== userDay) return false
    // Must meet user's quota requirement
    if (userQuota === -1) {
      if (plan.data.quota !== -1) return false
    } else if (plan.data.quota !== -1 && plan.data.quota < userQuota) {
      return false
    }
    const planCountries = new Set(
      plan.data.support_countries?.map((c) => c.toUpperCase()) || []
    )
    return userIso2s.every((iso2) => planCountries.has(iso2.toUpperCase()))
  }

  // ── Unlimited upgrade nudge: always show the cheapest unlimited plan ──
  // When user is in total mode, find the cheapest unlimited plan (daily or total)
  // that covers user's countries and matches user's day count. Show it as a purple
  // "upgrade to unlimited" card regardless of price gap.
  let hasUpgradeNudge = false
  if (!userDaily && userQuota !== -1 && userDay > 0) {
    const unlimitedCandidates = uniquePlans
      .filter((p) => {
        if (!p.amount || p.amount <= 0) return false
        if (p.data.quota !== -1) return false
        if (p.data.day !== userDay) return false
        const pc = new Set(p.data.support_countries?.map((c) => c.toUpperCase()) || [])
        return userIso2s.every((iso2) => pc.has(iso2.toUpperCase()))
      })
      .sort((a, b) => {
        // Prefer daily unlimited over total unlimited
        if (a.data.daily && !b.data.daily) return -1
        if (!a.data.daily && b.data.daily) return 1
        return a.amount - b.amount
      })

    const bestDailyUnlimited = unlimitedCandidates[0]
    if (bestDailyUnlimited) {
      const refPrice = cheapestExact < Infinity ? cheapestExact : bestDailyUnlimited.amount
      const totalGapCents = bestDailyUnlimited.amount - refPrice
      const gapPercent = refPrice > 0 ? Math.round((totalGapCents / refPrice) * 100) : 0
      const perDayCents = Math.round(totalGapCents / userDay)

      exactMatches.push({
        plan: bestDailyUnlimited,
        tier: "exact",
        explanation: explainUpgradeOffer(perDayCents, userDay, true, t),
        highlight: false,
        isUpgradeNudge: true,
        upgradePerDayCents: perDayCents,
        upgradeTotalGapCents: totalGapCents,
        upgradeGapPercent: gapPercent,
      })
      picked.add(pickedKey(bestDailyUnlimited))
      hasUpgradeNudge = true
    }
  }

  const nonUpgradeCount = () => inventoryOffers.filter((o) => !o.isUpgradeNudge).length

  for (const plan of [...uniquePlans].sort((a, b) => a.amount - b.amount)) {
    if (picked.has(pickedKey(plan))) continue
    if (!meetsBasic(plan)) continue

    const hasPhone = plan.tags?.phoneNumber && !exactMatches.some((o) => o.plan.tags?.phoneNumber)
    const hasHotspot = plan.tags?.hotspot && !exactMatches.some((o) => o.plan.tags?.hotspot)
    const isCheaper = plan.amount < cheapestExact

    // Must be either cheaper or have a unique feature not in exactMatches
    if (!isCheaper && !hasPhone && !hasHotspot) continue

    const info = explainOffer(plan, userIso2s, userDay, userQuota, t, refPrice)
    let explanation: string
    if (hasPhone) {
      explanation = t("offer-phone-number")
    } else if (hasHotspot) {
      explanation = t("offer-hotspot")
    } else {
      explanation = info.explanation
    }

    inventoryOffers.push({
      plan,
      tier: "inventory",
      explanation,
      highlight: false,
      savingsPercent: isCheaper ? info.savingsPercent : undefined,
      extraCountries: info.extraCountries,
    })
    picked.add(pickedKey(plan))
    if (nonUpgradeCount() >= 3) break
  }

  // ── Near matches: when exactMatches is empty, find plans with similar days ──
  const nearMatches: DualEngineOffer[] = []
  if (exactMatches.length === 0) {
    const nearCandidates: RecommendationPlan[] = []
    for (const plan of uniquePlans) {
      if (!meetsNearConstraints(plan, userIso2s, userDay, userQuota)) continue
      if (userDaily && !plan.data.daily) continue
      if (!userDaily && userQuota !== -1 && plan.data.daily) continue
      nearCandidates.push(plan)
    }
    // Sort by day distance (closest first), then by price
    nearCandidates.sort((a, b) => {
      const distA = Math.abs(a.data.day - userDay)
      const distB = Math.abs(b.data.day - userDay)
      if (distA !== distB) return distA - distB
      return a.amount - b.amount
    })

    // Pick up to 3 near-match offers, preferring different day values
    const nearDaysSeen = new Set<number>()
    for (const plan of nearCandidates) {
      if (nearMatches.length >= 3) break
      const pk = pickedKey(plan)
      if (picked.has(pk)) continue

      const dayDiff = plan.data.day - userDay
      const dayLabel = dayDiff > 0
        ? t("offer-near-diff-more", { count: dayDiff })
        : t("offer-near-diff-fewer", { count: Math.abs(dayDiff) })

      nearMatches.push({
        plan,
        tier: "near",
        explanation: t("offer-near-match", { days: plan.data.day, diff: dayLabel }),
        highlight: nearMatches.length === 0,
      })
      picked.add(pk)
      nearDaysSeen.add(plan.data.day)
    }
  }

  // Discover unique quota values from all returned plans
  const quotaSet = new Set<number>()
  for (const plan of uniquePlans) {
    if (plan.data.quota > 0 && !plan.data.daily) {
      quotaSet.add(plan.data.quota)
    }
  }
  const discoveredQuotas = Array.from(quotaSet).sort((a, b) => a - b)

  return { exactMatches, nearMatches, inventoryOffers, discoveredQuotas }
}

/**
 * Get dynamic quota options from discovered quotas.
 * Returns at most 6 options suitable for a slider/button UI.
 */
export function getDynamicQuotaOptions(
  discoveredQuotas: number[]
): Array<{ label: string; value: number }> {
  if (discoveredQuotas.length === 0) {
    // Fallback to default options
    return [
      { label: "1GB", value: 1024 },
      { label: "3GB", value: 3072 },
      { label: "5GB", value: 5120 },
      { label: "10GB", value: 10240 },
      { label: "20GB", value: 20480 },
      { label: "50GB", value: 51200 },
    ]
  }

  // Pick representative options: try to include smallest, largest, and spread
  const all = discoveredQuotas.filter((q) => q >= 500) // exclude tiny values
  if (all.length <= 8) {
    return all.map((q) => ({ label: formatQuota(q), value: q }))
  }

  // Pick 8 spread values
  const result: number[] = [all[0]]
  const step = (all.length - 1) / 7
  for (let i = 1; i < 7; i++) {
    result.push(all[Math.round(i * step)])
  }
  result.push(all[all.length - 1])

  // Deduplicate
  const unique = Array.from(new Set(result)).sort((a, b) => a - b)
  return unique.map((q) => ({ label: formatQuota(q), value: q }))
}

/**
 * Generate probe search params for quota discovery.
 * 4 parallel API calls covering low/mid/high/very-high total ranges.
 */
export function getProbeSearchParams(
  days: number
): Array<{ quota: number | string; day: number }> {
  return [
    { quota: 1024, day: days },
    { quota: 5120, day: days },
    { quota: 10240, day: days },
    { quota: 20480, day: days },
    { quota: 51200, day: days }, // CMLink supports up to 50GB
  ]
}

/**
 * Extract dynamic daily-mode options from probed plans.
 * Returns null if not enough options found (caller should fall back to defaults).
 */
export function getDynamicDailyOptions(
  plans: Array<{ data: { daily?: boolean; quota: number } }>
): Array<{ label: string; labelEN: string; value: number }> | null {
  const dailyQuotaSet = new Set<number>()
  for (const plan of plans) {
    if (plan.data.daily && plan.data.quota > 0) {
      dailyQuotaSet.add(plan.data.quota)
    }
  }

  if (dailyQuotaSet.size < 2) return null

  const sorted = Array.from(dailyQuotaSet).sort((a, b) => a - b)
  return sorted.map((q) => ({
    label: formatQuota(q, "无限流量"),
    labelEN: formatQuota(q),
    value: q,
  }))
}

// =============================================================================
// Plan exhaust behaviour (post-quota) — parsed from description text
// =============================================================================

/**
 * What happens once a plan's data allowance is used up.
 *
 * Source of truth is the API `description_en` text, e.g.
 *   "10GB total data, then throttled to 128kbps"  → throttle, "128Kbps"
 *   "3GB total data, service stops after reaching limit" → terminate
 *   "...unlimited data at full speed" → unlimited
 * yohoCare is deliberately NOT used: it does not encode exhaust behaviour
 * (yohoCare=false plans still throttle). Plans without a parseable description
 * return "unknown" and simply omit the speed-rule row.
 */
export type PlanExhaust = {
  mode: "throttle" | "terminate" | "unlimited" | "unknown"
  /** Display label of the throttled speed, e.g. "128Kbps" / "5Mbps"; null unless throttle. */
  throttleLabel: string | null
}

export function derivePlanExhaust(plan: RecommendationPlan): PlanExhaust {
  const desc = plan.data.description_en ?? ""
  const throttle = desc.match(/throttled?\s+to\s+(\d+)\s*(kbps|mbps)/i)
  if (throttle) {
    const unit = throttle[2].toLowerCase() === "mbps" ? "Mbps" : "Kbps"
    return { mode: "throttle", throttleLabel: `${throttle[1]}${unit}` }
  }
  if (/\bstops?\b|service stops/i.test(desc)) {
    return { mode: "terminate", throttleLabel: null }
  }
  if (plan.data.quota === -1 || /full speed/i.test(desc)) {
    return { mode: "unlimited", throttleLabel: null }
  }
  return { mode: "unknown", throttleLabel: null }
}

/** Whether to render the speed-rule row in plan details (only when the behaviour is known). */
export function hasSpeedRule(exhaust: PlanExhaust): boolean {
  return exhaust.mode === "throttle" || exhaust.mode === "terminate"
}
